import { ScrollText, Users, FileText, CheckCircle, AlertTriangle, Scale } from "lucide-react";
import GenericPracticePage from "@/components/GenericPracticePage";

const AffidavitsHeirship = () => {
  const mainContent = (
    <>
      <section className="py-20 bg-background">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-bold mb-6">Affidavits of Heirship in Texas Real Estate Transactions</h2>
            <p className="text-lg text-muted-foreground mb-8">
              An Affidavit of Heirship is a legal document used to establish ownership of real property when a person dies without a will (intestate) or when probate has not been completed. This sworn statement identifies the deceased person's heirs and their respective ownership interests in the property. In Texas, Affidavits of Heirship are commonly used to clear title and transfer property from a deceased owner to their rightful heirs without the need for formal probate proceedings. Our experienced attorneys prepare legally sound Affidavits of Heirship that meet Texas statutory requirements and satisfy title company standards, enabling smooth property transfers and real estate transactions.
            </p>

            <div className="bg-accent/10 border-l-4 border-accent p-6 rounded-r-lg mb-12">
              <p className="text-lg font-semibold mb-2">
                <AlertTriangle className="inline h-5 w-5 mr-2 text-accent" />
                When Do You Need an Affidavit of Heirship?
              </p>
              <p className="text-muted-foreground">
                If you've inherited property in Texas and the previous owner died without a will, or probate was never completed, you'll likely need an Affidavit of Heirship to establish clear title. This is especially important when you want to sell, refinance, or transfer the property. Without proper documentation, title companies won't insure the title, and buyers won't be able to obtain financing.
              </p>
            </div>

            <h3 className="text-3xl font-bold mb-8">What an Affidavit of Heirship Establishes</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
              <div className="bg-card p-6 rounded-lg border border-accent/20">
                <Users className="h-8 w-8 text-accent mb-4" />
                <h3 className="text-xl font-bold mb-3">Identity of Heirs</h3>
                <ul className="space-y-2">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-5 w-5 text-accent flex-shrink-0 mt-0.5" />
                    <span>Full legal names of all heirs</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-5 w-5 text-accent flex-shrink-0 mt-0.5" />
                    <span>Relationship to the deceased</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-5 w-5 text-accent flex-shrink-0 mt-0.5" />
                    <span>Each heir's ownership percentage</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-5 w-5 text-accent flex-shrink-0 mt-0.5" />
                    <span>Community vs separate property determination</span>
                  </li>
                </ul>
              </div>

              <div className="bg-card p-6 rounded-lg border border-accent/20">
                <FileText className="h-8 w-8 text-accent mb-4" />
                <h3 className="text-xl font-bold mb-3">Family History</h3>
                <ul className="space-y-2">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-5 w-5 text-accent flex-shrink-0 mt-0.5" />
                    <span>Marital status of deceased</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-5 w-5 text-accent flex-shrink-0 mt-0.5" />
                    <span>Names of spouses (current and former)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-5 w-5 text-accent flex-shrink-0 mt-0.5" />
                    <span>Children (including adopted and biological)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-5 w-5 text-accent flex-shrink-0 mt-0.5" />
                    <span>Parents and siblings information</span>
                  </li>
                </ul>
              </div>

              <div className="bg-card p-6 rounded-lg border border-accent/20">
                <Scale className="h-8 w-8 text-accent mb-4" />
                <h3 className="text-xl font-bold mb-3">Property Information</h3>
                <ul className="space-y-2">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-5 w-5 text-accent flex-shrink-0 mt-0.5" />
                    <span>Legal description of the property</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-5 w-5 text-accent flex-shrink-0 mt-0.5" />
                    <span>How and when deceased acquired property</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-5 w-5 text-accent flex-shrink-0 mt-0.5" />
                    <span>Property use and possession history</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-5 w-5 text-accent flex-shrink-0 mt-0.5" />
                    <span>Outstanding debts or liens</span>
                  </li>
                </ul>
              </div>

              <div className="bg-card p-6 rounded-lg border border-accent/20">
                <ScrollText className="h-8 w-8 text-accent mb-4" />
                <h3 className="text-xl font-bold mb-3">Affiant Qualifications</h3>
                <ul className="space-y-2">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-5 w-5 text-accent flex-shrink-0 mt-0.5" />
                    <span>Two disinterested witnesses required</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-5 w-5 text-accent flex-shrink-0 mt-0.5" />
                    <span>Personal knowledge of family history</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-5 w-5 text-accent flex-shrink-0 mt-0.5" />
                    <span>No interest in the property</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-5 w-5 text-accent flex-shrink-0 mt-0.5" />
                    <span>Must be notarized and recorded</span>
                  </li>
                </ul>
              </div>
            </div>

            <h3 className="text-3xl font-bold mb-6">Texas Intestate Succession Rules</h3>
            <p className="text-lg text-muted-foreground mb-6">
              Understanding who inherits property under Texas law is essential for preparing an accurate Affidavit of Heirship:
            </p>
            <div className="bg-card p-8 rounded-lg border border-accent/20 mb-12">
              <div className="space-y-4">
                <div>
                  <h4 className="font-bold text-lg mb-2">If deceased was married with children:</h4>
                  <p className="text-muted-foreground ml-6">• Community property: Surviving spouse inherits deceased's half; children inherit nothing</p>
                  <p className="text-muted-foreground ml-6">• Separate property: Surviving spouse gets 1/3, children share 2/3</p>
                </div>
                <div>
                  <h4 className="font-bold text-lg mb-2">If deceased was married without children:</h4>
                  <p className="text-muted-foreground ml-6">• Community property: Surviving spouse inherits all</p>
                  <p className="text-muted-foreground ml-6">• Separate property: Surviving spouse gets all unless deceased has parents/siblings</p>
                </div>
                <div>
                  <h4 className="font-bold text-lg mb-2">If deceased was unmarried with children:</h4>
                  <p className="text-muted-foreground ml-6">• Children inherit everything equally</p>
                </div>
                <div>
                  <h4 className="font-bold text-lg mb-2">If deceased had no spouse or children:</h4>
                  <p className="text-muted-foreground ml-6">• Property passes to parents, then siblings, then extended family</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-muted/30">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold mb-8 text-center">Our Affidavit of Heirship Process</h2>
            <div className="space-y-6">
              {[
                { step: "1", title: "Initial Consultation", desc: "Discuss family history, property details, and gather necessary information about the deceased and heirs." },
                { step: "2", title: "Research & Verification", desc: "Verify death records, marriage certificates, birth certificates, and property records to confirm heir identities." },
                { step: "3", title: "Identify Witnesses", desc: "Locate two qualified disinterested witnesses with personal knowledge of the family and property history." },
                { step: "4", title: "Draft Affidavit", desc: "Prepare comprehensive Affidavit of Heirship that meets Texas statutory requirements and title company standards." },
                { step: "5", title: "Execution & Notarization", desc: "Coordinate signing by witnesses before notary public to ensure proper execution." },
                { step: "6", title: "Recording", desc: "File the affidavit with the county clerk's office in the county where the property is located." },
                { step: "7", title: "Title Clearance", desc: "Work with title companies to ensure affidavit satisfies their underwriting requirements for clear title." }
              ].map((item) => (
                <div key={item.step} className="flex gap-4 items-start bg-card p-6 rounded-lg">
                  <div className="flex-shrink-0 w-10 h-10 rounded-full bg-gradient-gold-shine text-primary font-bold flex items-center justify-center">
                    {item.step}
                  </div>
                  <div>
                    <h4 className="text-lg font-bold mb-1">{item.title}</h4>
                    <p className="text-muted-foreground">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-background">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold mb-8">Limitations and Alternatives</h2>
            <div className="bg-accent/10 p-6 rounded-lg mb-8">
              <h3 className="text-xl font-bold mb-4">When an Affidavit of Heirship May Not Be Sufficient</h3>
              <ul className="space-y-2">
                <li className="flex items-start gap-2">
                  <AlertTriangle className="h-5 w-5 text-accent flex-shrink-0 mt-0.5" />
                  <span>Complex family situations with disputed heirs</span>
                </li>
                <li className="flex items-start gap-2">
                  <AlertTriangle className="h-5 w-5 text-accent flex-shrink-0 mt-0.5" />
                  <span>Property with significant value requiring formal probate</span>
                </li>
                <li className="flex items-start gap-2">
                  <AlertTriangle className="h-5 w-5 text-accent flex-shrink-0 mt-0.5" />
                  <span>Outstanding debts or creditor claims against the estate</span>
                </li>
                <li className="flex items-start gap-2">
                  <AlertTriangle className="h-5 w-5 text-accent flex-shrink-0 mt-0.5" />
                  <span>When absolute certainty of title is required (some lenders may not accept)</span>
                </li>
              </ul>
            </div>

            <h3 className="text-2xl font-bold mb-6">Alternative Options</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-card p-6 rounded-lg border border-accent/20">
                <h4 className="font-bold text-lg mb-2">Small Estate Affidavit</h4>
                <p className="text-muted-foreground">For estates under $75,000 with no real property, a Small Estate Affidavit may be simpler and faster.</p>
              </div>
              <div className="bg-card p-6 rounded-lg border border-accent/20">
                <h4 className="font-bold text-lg mb-2">Formal Probate</h4>
                <p className="text-muted-foreground">Provides absolute title clarity through court supervision and may be necessary for complex situations.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-muted/30">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold mb-12 text-center">Why Choose Our Services?</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="text-center p-6">
                <div className="flex justify-center mb-4">
                  <ScrollText className="h-12 w-12 text-accent" />
                </div>
                <h3 className="text-xl font-bold mb-2">Legally Sound Documents</h3>
                <p className="text-muted-foreground">Affidavits that meet Texas statutory requirements and satisfy title companies</p>
              </div>
              <div className="text-center p-6">
                <div className="flex justify-center mb-4">
                  <Users className="h-12 w-12 text-accent" />
                </div>
                <h3 className="text-xl font-bold mb-2">Family Law Expertise</h3>
                <p className="text-muted-foreground">Deep understanding of Texas intestate succession and heir identification</p>
              </div>
              <div className="text-center p-6">
                <div className="flex justify-center mb-4">
                  <FileText className="h-12 w-12 text-accent" />
                </div>
                <h3 className="text-xl font-bold mb-2">Complete Service</h3>
                <p className="text-muted-foreground">From research and drafting to recording and title clearance</p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );

  return (
    <GenericPracticePage
      title="Affidavits of Heirship"
      subtitle="Establishing Property Ownership Through Inheritance in Texas"
      icon={<ScrollText className="h-16 w-16" />}
      mainContent={mainContent}
    />
  );
};

export default AffidavitsHeirship;